import classNames from 'classnames';
import React from 'react';

import { BenefitsAccordionItemLinkFragmentFragment, ImageFragmentFragment } from '../../../__generated__/graphql';
import Divider from '../../../components/Divider';
import GradientBorderCard from '../../../components/GradientBorderCard';
import Svg from '../../../components/Svg';
import { Paragraph, TextLink } from '../../../components/Text';
import { AccordionAnimation } from '../../../utils/animations/accordion';
import { usePrefersReducedMotion } from '../../../utils/usePrefersReducedMotion';

import style from './benefits-accordion-item.module.css';

interface BenefitsAccordionItemProps {
  description: null | string | undefined;
  image: ImageFragmentFragment | null | undefined;
  links: BenefitsAccordionItemLinkFragmentFragment['links'];
}

const BenefitsAccordionItem = ({
  description,
  image,
  links,
}: BenefitsAccordionItemProps) => {
  const detailsId = (image?.node.id ?? '').replace(/=/g, '');

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    const detailsElement = document.querySelector<HTMLDetailsElement>(`#${detailsId}`);
    if (detailsElement) {
      const animation = new AccordionAnimation(detailsElement, { prefersReducedMotion });

      return () => {
        animation.removeEventListener();
      };
    }
  }, [detailsId, prefersReducedMotion]);

  return (
    <GradientBorderCard backgroundColor="white">
      <details
        className={style.details}
        id={detailsId}
      >
        <summary className={classNames(style.summary, AccordionAnimation.summaryClassName)}>
          <Svg
            containerClassName={style.benefitsAccordionItemImage}
            image={image}
          />

          <div className={style.plus}>
            <div className={style.plusAxis} />
            <div className={classNames(style.plusAxis, style.plusY)} />
          </div>
        </summary>

        <div className={classNames(style.content, AccordionAnimation.contentClassName)}>
          <Paragraph className={style.benefitsAccordionDescription}>
            {description}
          </Paragraph>

          {(links?.length ?? 0) > 0 && (
            <>
              <Divider
                thickness={2}
                variant="gradient"
              />

              <div className={style.benefitsAccordionLinks}>
                <Paragraph
                  fontSize={14}
                  fontWeight={700}
                  uppercase
                >
                  Learn more about:
                </Paragraph>

                {links?.map(link => {
                  if (link) {
                    return (
                      <TextLink
                        fontColor="iris"
                        fontSize={16}
                        fontWeight={700}
                        id={link.linkId ?? undefined}
                        key={link.link?.title}
                        linkFragment={{
                          ...link.link,
                          title: `${link.link?.title ?? ''} ›`,
                        }}
                      />
                    );
                  }
                })}
              </div>
            </>
          )}
        </div>
      </details>
    </GradientBorderCard>
  );
};

export default BenefitsAccordionItem;
